import React from 'react';
import { Card, Space, DatePicker, Input, Select, message } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import StandardTable from '../../../components/common/StandardTable';
import { fetchSqlDataWithQuery } from '../../../services/api';
import { useGetMatClntWarehouses, useGetMatClntWhInventory } from '../../../hooks/genericHooks';
import { formatNumberSmart } from '../../../utils/numberFormat';
import { SearchOutlined } from '@ant-design/icons';
import MatClntMasterMaterialSelect from '../../../components/modals/MatClntMasterMaterialSelect';
import Button from '../../../components/common/Button';

export type MatClntOpType = 'incoming' | 'transfer' | 'issue' | 'assign' | 'return' | 'sendback' | 'adjustment';

export interface CoreTxnListFilters {
  dateRange?: [string, string] | null;
  reference?: string;
  itemcode?: string | null;
  matText?: string | null; // filters description and notes
  whFrom?: number | null;
  whTo?: number | null;
  assignee?: string | null;
  issuedTo?: string | null;
  createdBy?: string | null;
}

interface MatClntCoreTransactionListProps {
  title: string;
  opTypes: MatClntOpType[];
  defaultFilters?: CoreTxnListFilters;
}

const { RangePicker } = DatePicker;

export default function MatClntCoreTransactionList({ title, opTypes, defaultFilters }: MatClntCoreTransactionListProps) {
  const [filters, setFilters] = React.useState<CoreTxnListFilters>(() => ({
    dateRange: defaultFilters?.dateRange ?? null,
    reference: defaultFilters?.reference ?? '',
    itemcode: defaultFilters?.itemcode ?? null,
    matText: null,
    whFrom: defaultFilters?.whFrom ?? null,
    whTo: defaultFilters?.whTo ?? null,
    assignee: defaultFilters?.assignee ?? null,
    issuedTo: null,
    createdBy: defaultFilters?.createdBy ?? null,
  }));
  const [loading, setLoading] = React.useState(false);
  const [allRows, setAllRows] = React.useState<any[]>([]);

  // Warehouses
  const { data: whDataRaw = [] } = useGetMatClntWarehouses() as any;
  const warehouses: any[] = Array.isArray(whDataRaw) ? whDataRaw : (whDataRaw?.data ?? []);
  const whMap = React.useMemo(() => {
    const m = new Map<number, string>();
    (warehouses || []).forEach((w: any) => m.set(Number(w.id), String(w.name)));
    return m;
  }, [warehouses]);

  // Material description/size map from inventory
  const { data: invRaw = [] } = useGetMatClntWhInventory() as any;
  const invList: any[] = Array.isArray(invRaw) ? invRaw : (invRaw?.data ?? []);
  const matInfoMap = React.useMemo(() => {
    const m = new Map<string, { description?: string | null; size_text?: string | null }>();
    (invList || []).forEach((row: any) => {
      const code = String(row.itemcode || '').trim();
      if (!code) return;
      if (!m.has(code)) m.set(code, { description: row.description ?? null, size_text: (row as any)?.size_text ?? null });
    });
    return m;
  }, [invList]);

  // Material select modal state
  const [matSelectOpen, setMatSelectOpen] = React.useState(false);

  const escape = (v: string) => v.replace(/'/g, "''");

  const buildWhere = React.useCallback(() => {
    const conds: string[] = [];
    if (opTypes?.length) {
      const inList = opTypes.map(t => `'${escape(String(t))}'`).join(',');
      conds.push(`op_type IN (${inList})`);
    }
    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      conds.push(`txn_date >= '${escape(filters.dateRange[0])}' AND txn_date <= '${escape(filters.dateRange[1])}'`);
    }
    return conds.length ? `WHERE ${conds.join(' AND ')}` : '';
  }, [opTypes, filters.dateRange]);

  const runQuery = React.useCallback(async () => {
    const where = buildWhere();
    const sql = `
      SELECT id, op_type, txn_date, reference_no, from_warehouse_id, to_warehouse_id,
             itemcode, qty, unit, assignee_id, issued_to, created_by, notes
      FROM mat_clnt_txns
      ${where}
      ORDER BY txn_date DESC, id DESC
    `;
    try {
      setLoading(true);
      const data = await fetchSqlDataWithQuery(sql);
      const arr = Array.isArray(data) ? data : (data?.rows || data?.data || []);
      setAllRows(arr || []);
    } catch (e) {
      message.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [buildWhere]);

  React.useEffect(() => { runQuery(); }, [runQuery]);

  const handleDateChange: RangePickerProps['onChange'] = (vals) => {
    if (!vals || !Array.isArray(vals)) { setFilters((p) => ({ ...p, dateRange: null })); return; }
    setFilters((p) => ({ ...p, dateRange: [vals[0]?.format('YYYY-MM-DD') || '', vals[1]?.format('YYYY-MM-DD') || ''] as [string, string] }));
  };

  // Client-side filtering for cascades
  const filteredRows = React.useMemo(() => {
    return (allRows || []).filter((r) => {
      if (filters.reference && r.reference_no !== filters.reference) return false;
      if (filters.itemcode) {
        const needle = String(filters.itemcode).trim().toLowerCase();
        const codeHay = String(r.itemcode ?? '').toLowerCase();
        if (!codeHay.includes(needle)) return false;
      }
      if (filters.matText) {
        const needle = String(filters.matText).trim().toLowerCase();
        const descHay = String(matInfoMap.get(String(r.itemcode))?.description || '').toLowerCase();
        const notesHay = String(r.notes || '').toLowerCase();
        if (!(descHay.includes(needle) || notesHay.includes(needle))) return false;
      }
      if (filters.whFrom && Number(r.from_warehouse_id) !== Number(filters.whFrom)) return false;
      if (filters.whTo && Number(r.to_warehouse_id) !== Number(filters.whTo)) return false;
      if (filters.assignee) {
        const s = String(filters.assignee).toLowerCase();
        const a = (r.assignee_id ? String(r.assignee_id) : '').toLowerCase();
        const i = (r.issued_to ? String(r.issued_to) : '').toLowerCase();
        if (a !== s && i !== s) return false;
      }
      if (filters.issuedTo && String(r.issued_to || '').toLowerCase() !== String(filters.issuedTo).toLowerCase()) return false;
      if (filters.createdBy && String(r.created_by || '').toLowerCase() !== String(filters.createdBy).toLowerCase()) return false;
      return true;
    });
  }, [allRows, filters, matInfoMap]);

  const distinct = (vals: any[]) => Array.from(new Set(vals.filter(v => v !== undefined && v !== null && v !== '')));

  const optionsFor = (field: 'reference' | 'whFrom' | 'whTo' | 'assignee' | 'issuedTo' | 'createdBy') => {
    const base = (allRows || []).filter((r) => {
      if (field !== 'reference' && filters.reference && r.reference_no !== filters.reference) return false;
      if (field !== 'whFrom' && filters.whFrom && Number(r.from_warehouse_id) !== Number(filters.whFrom)) return false;
      if (field !== 'whTo' && filters.whTo && Number(r.to_warehouse_id) !== Number(filters.whTo)) return false;
      if (field !== 'assignee' && filters.assignee) {
        const s = String(filters.assignee).toLowerCase();
        const a = (r.assignee_id ? String(r.assignee_id) : '').toLowerCase();
        const i = (r.issued_to ? String(r.issued_to) : '').toLowerCase();
        if (a !== s && i !== s) return false;
      }
      if (field !== 'issuedTo' && filters.issuedTo && String(r.issued_to || '').toLowerCase() !== String(filters.issuedTo).toLowerCase()) return false;
      if (field !== 'createdBy' && filters.createdBy && String(r.created_by || '').toLowerCase() !== String(filters.createdBy).toLowerCase()) return false;
      if (filters.itemcode) {
        const needle = String(filters.itemcode).trim().toLowerCase();
        const codeHay = String(r.itemcode ?? '').toLowerCase();
        const descHay = String(matInfoMap.get(String(r.itemcode))?.description || '').toLowerCase();
        if (!(codeHay.includes(needle) || descHay.includes(needle))) return false;
      }
      return true;
    });

    switch (field) {
      case 'reference':
        return distinct(base.map(r => r.reference_no)).map((v: string) => ({ value: v, label: v }));
      case 'whFrom':
        return distinct(base.map(r => r.from_warehouse_id)).map((v: number) => ({ value: v, label: whMap.get(Number(v)) || String(v) }));
      case 'whTo':
        return distinct(base.map(r => r.to_warehouse_id)).map((v: number) => ({ value: v, label: whMap.get(Number(v)) || String(v) }));
      case 'assignee': {
        const ids = distinct(base.map(r => r.assignee_id));
        const names = distinct(base.map(r => r.issued_to));
        const merged = distinct([...ids, ...names].map(String));
        return merged.map(v => ({ value: v, label: v }));
      }
      case 'issuedTo':
        return distinct(base.map(r => r.issued_to)).map((v: string) => ({ value: v, label: v }));
      case 'createdBy':
        return distinct(base.map(r => r.created_by)).map((v: string) => ({ value: v, label: v }));
    }
  };

  const columns = React.useMemo(() => [
    { title: 'Type', dataIndex: 'op_type', key: 'op_type', width: 120 },
    { title: 'Date', dataIndex: 'txn_date', key: 'txn_date', width: 110, render: (v: any) => (v ? dayjs(v).format('YYYY-MM-DD') : '') },
    { title: 'Reference', dataIndex: 'reference_no', key: 'reference_no', width: 160 },
    { title: 'From WH', dataIndex: 'from_warehouse_id', key: 'from_warehouse_id', width: 160, render: (v: number) => whMap.get(Number(v)) || v },
    { title: 'To WH', dataIndex: 'to_warehouse_id', key: 'to_warehouse_id', width: 160, render: (v: number) => whMap.get(Number(v)) || v },
    { title: 'Item Code', dataIndex: 'itemcode', key: 'itemcode', width: 120 },
    { title: 'Description', key: 'description', width: 260, ellipsis: true, render: (_: any, r: any) => matInfoMap.get(String(r.itemcode))?.description || '' },
    { title: 'Size', key: 'size_text', width: 140, render: (_: any, r: any) => matInfoMap.get(String(r.itemcode))?.size_text || '' },
    { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 110, align: 'right', render: (v: unknown) => formatNumberSmart(v) },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 90 },
    { title: 'Assignee ID', dataIndex: 'assignee_id', key: 'assignee_id', width: 160, align: 'left' },
    { title: 'Issued To', dataIndex: 'issued_to', key: 'issued_to', width: 180 },
    { title: 'Created By', dataIndex: 'created_by', key: 'created_by', width: 140 },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', width: 220 },
  ], [whMap, matInfoMap]);

  return (
    <Card title={title}>
      <Space wrap style={{ marginBottom: 12 }}>
        <RangePicker
          value={
            filters.dateRange
              ? [dayjs(filters.dateRange[0] || undefined), dayjs(filters.dateRange[1] || undefined)]
              : null
          }
          onChange={handleDateChange}
        />
        <Select
          placeholder="Reference"
          allowClear
          showSearch
          value={filters.reference || undefined}
          onChange={(v) => setFilters((p) => ({ ...p, reference: (v || '') as string }))}
          options={optionsFor('reference')}
          style={{ width: 200 }}
          filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
        />
        <Space.Compact>
          <Input
            placeholder="Item Code"
            value={filters.itemcode ?? ''}
            onChange={(e) => setFilters((p) => ({ ...p, itemcode: (e.target.value || '').trim() || null }))}
            style={{ width: 180 }}
            allowClear
          />
          <Button icon={<SearchOutlined />} onClick={() => setMatSelectOpen(true)} aria-label="Select material" />
        </Space.Compact>
        <Input
          placeholder="Description / Notes"
          value={filters.matText ?? ''}
          onChange={(e) => setFilters((p) => ({ ...p, matText: (e.target.value ?? '') }))}
          style={{ width: 240 }}
          allowClear
        />
        <Select
          placeholder="From WH"
          allowClear
          value={filters.whFrom ?? undefined}
          onChange={(v) => setFilters((p) => ({ ...p, whFrom: (v as number) ?? null }))}
          options={(warehouses || []).map((w: any) => ({ value: Number(w.id), label: String(w.name) }))}
          style={{ width: 180 }}
          showSearch
          filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
        />
        <Select
          placeholder="To WH"
          allowClear
          value={filters.whTo ?? undefined}
          onChange={(v) => setFilters((p) => ({ ...p, whTo: (v as number) ?? null }))}
          options={(warehouses || []).map((w: any) => ({ value: Number(w.id), label: String(w.name) }))}
          style={{ width: 180 }}
          showSearch
          filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
        />
        <Select
          placeholder="Assignee"
          allowClear
          value={filters.assignee ?? undefined}
          onChange={(v) => setFilters((p) => ({ ...p, assignee: (v as string) ?? null }))}
          options={optionsFor('assignee')}
          style={{ width: 220 }}
          showSearch
          filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
        />
        <Select
          placeholder="Created By"
          allowClear
          value={filters.createdBy ?? undefined}
          onChange={(v) => setFilters((p) => ({ ...p, createdBy: (v as string) ?? null }))}
          options={optionsFor('createdBy')}
          style={{ width: 200 }}
          showSearch
          filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
        />
        <Select
          placeholder="Issued To"
          allowClear
          value={filters.issuedTo ?? undefined}
          onChange={(v) => setFilters((p) => ({ ...p, issuedTo: (v as string) ?? null }))}
          options={optionsFor('issuedTo')}
          style={{ width: 200 }}
          showSearch
          filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
        />
        <Button type="primary" onClick={runQuery}>Search</Button>
        <Button onClick={() => setFilters({ dateRange: null, reference: '', itemcode: null, matText: null, whFrom: null, whTo: null, assignee: null, issuedTo: null, createdBy: null })}>Reset</Button>
      </Space>

      <StandardTable
        columns={columns as any}
        dataSource={filteredRows as any}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
      />

      <MatClntMasterMaterialSelect
        open={matSelectOpen}
        onCancel={() => setMatSelectOpen(false)}
        onOk={(selected) => {
          const first = Array.isArray(selected) && selected.length > 0 ? selected[0] : undefined;
          if (first && first.itemcode) {
            setFilters((p) => ({ ...p, itemcode: String(first.itemcode) }));
          }
          setMatSelectOpen(false);
        }}
        title="Select Client Material"
      />
    </Card>
  );
}
